// stripe.service.ts
import { Injectable } from '@angular/core';
import { BehaviorSubject, from, Observable, throwError } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import {
  CanMakePaymentResult,
  loadStripe,
  PaymentRequest,
  Stripe,
  StripeCardCvcElement,
  StripeCardExpiryElement,
  StripeCardNumberElement,
  StripeElements,
  StripePaymentRequestButtonElement,
  StripePaymentRequestButtonElementOptions,
} from '@stripe/stripe-js';
import { AkitaPaymentQuery } from '@app/akita/api/payments/state/payment.query';
import { AkitaCheckoutQuery } from '@app/akita/api/checkout/state/checkout.query';
import { StripePaymentRequestService } from './stripe-payment-request.service';
import { PriceModel } from '@app/shared/models/api/price.model';

@Injectable({ providedIn: 'root' })
export class StripeActionService {
  private stripe!: Stripe;
  private elements!: StripeElements;
  private cardNumber!: StripeCardNumberElement;
  private cardExpiry!: StripeCardExpiryElement;
  private cardCvc!: StripeCardCvcElement;
  private applePayButton!: StripePaymentRequestButtonElement;
  /*   private expressCheckout!: StripeExpressCheckoutElement;
   */ private stripePaymentRequest!: PaymentRequest;
  private canMakePaymentResult: CanMakePaymentResult | null = null;

  private readonly paymentRequestSubject: BehaviorSubject<PaymentRequest | null> =
    new BehaviorSubject(null as any);

  constructor(
    private readonly akitaPaymentQuery: AkitaPaymentQuery,
    private readonly akitaCheckoutQuery: AkitaCheckoutQuery,
    private readonly stripePaymentRequestService: StripePaymentRequestService
  ) {}

  public selectPaymentRequest(): Observable<PaymentRequest | null> {
    return this.paymentRequestSubject.asObservable();
  }

  public initializeStripe(
    publishableKey: string
  ): Observable<CanMakePaymentResult | null> {
    return from(loadStripe(publishableKey)).pipe(
      switchMap((stripe) => {
        if (!stripe) {
          throw new Error('Stripe failed to load');
        }
        this.stripe = stripe;

        const session = this.akitaCheckoutQuery.session;
        const discountPrice = {
          amount: session?.discount?.amount || 0,
          currency: session?.discount?.currency,
        } as PriceModel;
        const paymentRequestOptions =
          this.stripePaymentRequestService.generatePaymentRequestOptions(
            session?.order,
            session?.insurances,
            false,
            'MANUAL',
            0,
            0,
            false,
            discountPrice,
            session?.protectionPrice
          );

        this.stripePaymentRequest = this.stripe.paymentRequest(paymentRequestOptions);
        this.paymentRequestSubject.next(this.stripePaymentRequest);
        return this.stripePaymentRequest.canMakePayment();
      }),
      map((result: CanMakePaymentResult | null) => {
        this.canMakePaymentResult = result;
        console.log('Can make payment result:', result);
        return result;
      }),
      catchError((err: unknown) => {
        console.error('Error initializing Stripe:', err);
        return throwError(() => err);
      })
    );
  }

  public mountStripeElements(onlyApplePay?: boolean | null): Observable<boolean> {
    const stripeReady$ = /* this.stripe
      ? of(this.stripe)
      : */ this.initializeStripe(this.akitaPaymentQuery.getStripeKeyWithFallback()).pipe(
      map(() => this.stripe)
    );

    return stripeReady$.pipe(
      map(() => {
        console.log('Stripe is ready, mounting elements... apple pay:', onlyApplePay);
        this.elements = this.stripe.elements();

        if (!onlyApplePay) {
          this.cardNumber = this.elements.create('cardNumber');
          this.cardExpiry = this.elements.create('cardExpiry');
          this.cardCvc = this.elements.create('cardCvc');

          this.cardNumber.mount('#card-number');
          this.cardExpiry.mount('#card-expiry');
          this.cardCvc.mount('#card-cvc');

          this.cardNumber.on('change', () => {});
        }

        if (this.canMakePaymentResult?.applePay && this.stripePaymentRequest !== null) {
          this.applePayButton = this.elements.create('paymentRequestButton', {
            paymentRequest: this.stripePaymentRequest,
          } as StripePaymentRequestButtonElementOptions);
          this.applePayButton.mount('#stripe-apple-pay-button');
        }
        console.log('Stripe Elements mounted successfully', this.canMakePaymentResult);

        return this.canMakePaymentResult?.applePay || false;
      })
    );
  }

  public submitCardPayment(paymentIntentId: string, userName: string): Observable<any> {
    if (!this.stripe) {
      return throwError(() => new Error('Stripe is not initialized'));
    }
    return from(
      this.stripe.confirmCardPayment(paymentIntentId, {
        payment_method: {
          card: this.cardNumber,
          billing_details: {
            name: userName, // Replace with actual customer name
          },
        },
      })
    ).pipe(
      map((result) => {
        if (result.error) {
          throw new Error(result.error.message);
        }
        return result;
      }),
      catchError((err: unknown) => throwError(() => err))
    );
  }

  // for Google Pay
  public submitWalletPayment(paymentIntentId: string, token: string): Observable<any> {
    if (!this.stripe) {
      return throwError(() => new Error('Stripe is not initialized'));
    }

    return from(
      this.stripe.confirmCardPayment(paymentIntentId, {
        payment_method: {
          card: {
            token: token,
          },
        },
      })
    ).pipe(
      map((result) => {
        if (result.error) {
          throw new Error(result.error.message);
        }
        return result;
      }),
      catchError((err: unknown) => throwError(() => err))
    );
  }

  // for Apple Pay
  public submitPaymentRequest(
    paymentIntentId: string,
    paymentMethodId: string
  ): Observable<any> {
    if (!this.stripe) {
      return throwError(() => new Error('Stripe is not initialized'));
    }

    return from(
      this.stripe.confirmCardPayment(paymentIntentId, {
        payment_method: paymentMethodId,
      })
    ).pipe(
      map((result) => {
        if (result.error) {
          throw new Error(result.error.message);
        }
        return result;
      }),
      catchError((err: unknown) => throwError(() => err))
    );
  }
}
